import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { EmptyState, ListTable, Panel, StatusChip, type ListColumn, type StatusTone } from '../../../ui-kit';
import { fetchCcIntegrations, type CcIntegration } from '../../../services/carrierCorrespondenceService';
import { DIALER_PARTS, KlarisMark } from '../rocco/IntegrationPicker';
import { WarehouseLogo } from '../policy-servicing/logos';
import { useLive } from '../rocco/live';

function stateOf(row: CcIntegration): { tone: StatusTone; label: string } {
  return row.connected ? { tone: 'good', label: 'Connected' } : { tone: 'neutral', label: 'Not connected' };
}

function logoOf(row: CcIntegration): React.ReactNode {
  if (row.id === 'warehouse') return <WarehouseLogo name={row.name} />;
  return <KlarisMark size={22} />;
}

/** Other systems that take the same place as the ones in use. */
const ALSO_WORKS_WITH: Record<string, string> = {
  warehouse: 'Redshift, Snowflake, BigQuery or Postgres',
  dialer: 'Regal, Aircall, Genesys or Dialpad',
};

/**
 * What this agent is joined to: the warehouse the cases are read from, and the
 * dialer that places the calls.
 *
 * Drawn exactly as the other agents' are (same table, columns and logos),
 * because it is the same question on every agent. The carrier numbers are not
 * a system the workspace connects, so they live on Agent Configuration.
 */
export default function IntegrationsPage() {
  const { data, error, loading } = useLive(fetchCcIntegrations, 10_000);
  if (error && !data) return <EmptyState message={error} />;

  const columns: ListColumn<CcIntegration>[] = [
    {
      key: 'name',
      header: 'System',
      grow: 1.2,
      minWidth: 200,
      render: (row) => (
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Box sx={{ display: 'flex', flexShrink: 0 }}>{logoOf(row)}</Box>
          <Stack sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>{row.name}</Typography>
            {row.built_on ? (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                {row.built_on.map((part, i) => (
                  <React.Fragment key={part}>
                    {i > 0 ? <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>+</Typography> : null}
                    {DIALER_PARTS[part] ? <Box sx={{ display: 'flex' }}>{DIALER_PARTS[part]}</Box> : null}
                    <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>{part}</Typography>
                  </React.Fragment>
                ))}
              </Stack>
            ) : null}
          </Stack>
        </Stack>
      ),
    },
    {
      key: 'role',
      header: 'Does',
      width: 190,
      render: (row) => (
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary, overflowWrap: 'anywhere' })}>
          {row.role}
        </Typography>
      ),
    },
    {
      key: 'state',
      header: 'State',
      width: 150,
      render: (row) => <StatusChip {...stateOf(row)} />,
    },
    {
      key: 'activity',
      header: 'Today',
      grow: 2,
      minWidth: 220,
      render: (row) => (
        <Stack sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, overflowWrap: 'anywhere' })}>
            {row.detail}
          </Typography>
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
            Also works with {ALSO_WORKS_WITH[row.id] ?? '—'}
          </Typography>
        </Stack>
      ),
    },
  ];

  return (
    <Panel title="Connected systems" subtitle="Cases read, carriers dialled · as they are now">
      <ListTable
        columns={columns}
        rows={data?.integrations ?? []}
        getRowKey={(row) => row.id}
        loading={loading}
        emptyMessage="Nothing connected."
      />
    </Panel>
  );
}
